import { z } from 'zod';

import { ACCELERATOR_UNITS_SOURCES } from '../carbon-methodology/derive-input.js';
import type { UsageLocationRung } from './usage-location.js';

/**
 * `input_provenance` schema + weakest-of-three accuracy-tier derivation +
 * reporting-eligibility enum (SIL-341, T3 of
 * resources/plans/use-phase-carbon.md §"Accuracy tiers").
 *
 * `asset_use_phase_snapshot.input_provenance` (707) is the AUTHORITATIVE
 * record; there is deliberately no stored `accuracy_tier` column (707's
 * header / the plan's rationale: the row is immutable, so a stored tier
 * would permanently strand historical rows the moment the rules change).
 * Everything here is derived at read time from a parsed `input_provenance`
 * blob — same discipline as `shouldShowCarbonClaim` /
 * `deriveCarbonCompleteness` (`dto/carbon.ts`), which store nothing either.
 *
 * No DB access. The ingestion CLI (SIL-343) builds and writes the
 * `input_provenance` object; `apps/api` reads a stored row, parses it with
 * `usePhaseInputProvenanceSchema`, and calls the two derive* functions below
 * on every request — never once at write time and cached.
 */

// ---------------------------------------------------------------------------
// input_provenance schema
// ---------------------------------------------------------------------------

export const POWER_BASIS_VALUES = ['telemetry_measured', 'archetype_default'] as const;
export type UsePhasePowerBasis = (typeof POWER_BASIS_VALUES)[number];

export const PUE_SOURCE_VALUES = ['customer_dc', 'org', 'industry_default'] as const;
export type UsePhasePueSource = (typeof PUE_SOURCE_VALUES)[number];

/**
 * Mirrors `UsageLocationRung` (`usage-location.ts`) as a Zod enum so
 * `input_provenance` can validate the rung it was resolved from. Deriving a
 * tier reuses that rung directly — it never re-derives location provenance.
 * The `satisfies` check below fails to compile if the two ever drift.
 */
export const usageLocationRungSchema = z.enum([
  'region_country_code',
  'data_center_address_country',
  'seller_org_address_country',
  'default_wor',
]);
type _RungSchemaMatchesType = z.infer<typeof usageLocationRungSchema> extends UsageLocationRung
  ? UsageLocationRung extends z.infer<typeof usageLocationRungSchema>
    ? true
    : never
  : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _rungSchemaMatchesType: _RungSchemaMatchesType = true;

/**
 * The full audit record behind a tier/eligibility derivation. Every field
 * the plan names is present:
 *   - power basis + observation window + daily coverage
 *   - grid-factor source (the `UsageLocationRung`)
 *   - PUE value + source
 *   - duty-cycle source
 *   - hardware-identity defaults inherited from the embodied pipeline
 *     (`cpu.name` / `gpu.archetype` — same shape as the embodied pipeline's
 *     `defaultedFields`, `apps/ingestion/src/carbon/derive-input.ts`)
 *   - `org_is_test` (decision 12 / Accepted risks — carried for audit, never
 *     gates anything)
 *
 * `dutyCycle` and `hardwareIdentityDefaults` are NOT tier inputs (only
 * power/location/PUE are, per the weakest-of-three rule) — they are carried
 * through for audit completeness and future surfaces, exactly as the plan
 * specifies for `input_provenance`.
 */
export const usePhaseInputProvenanceSchema = z.object({
  power: z.object({
    basis: z.enum(POWER_BASIS_VALUES),
    /** ISO date (yyyy-mm-dd) — mirrors `asset_use_phase_snapshot.observation_start`. */
    observationStart: z.string().nullable(),
    /** ISO date (yyyy-mm-dd) — mirrors `asset_use_phase_snapshot.observation_end`. */
    observationEnd: z.string().nullable(),
    /** Count of calendar days in the window that have >=1 telemetry row. */
    daysWithData: z.number().int().nonnegative(),
    /** Calendar days in the window (the denominator) — NOT days-with-a-row;
     * see the plan's telemetry-keying note on why the two diverge exactly
     * when coverage is imperfect. */
    calendarDaysInWindow: z.number().int().nonnegative(),
  }),
  location: z.object({
    /** The rung `resolveUsageLocation` returned — consumed, never re-derived. */
    rung: usageLocationRungSchema,
  }),
  pue: z.object({
    value: z.number(),
    source: z.enum(PUE_SOURCE_VALUES),
  }),
  dutyCycle: z.object({
    /** Free-text provenance for `usage.use_time_ratio` (e.g. 'telemetry_uptime',
     * 'archetype_default'). Not a tier input — audit-only. */
    source: z.string(),
  }),
  /** Same shape as the embodied pipeline's `defaultedFields`
   * (`derive-input.ts`): keys are among `'cpu.name'` / `'gpu.archetype'`,
   * values are a human-readable reason. Empty object = nothing defaulted. */
  hardwareIdentityDefaults: z.record(z.string(), z.string()).default({}),
  /**
   * SIL-388/389 — accelerator use-phase power on the archetype-default path,
   * for gpu-bearing servers only. `null` whenever it does not apply: no GPU
   * on this asset, power_basis = 'telemetry_measured' (SIL-389's hard
   * double-counting guard — measured whole-system watts already include GPU
   * draw), or the resolved GPU model has no TDP-table entry (draw stays
   * omitted; see `defaultedFields['gpu.tdp']` on the embodied-style
   * `hardwareIdentityDefaults` sibling above for that case instead).
   *
   * `populationAssumption` and `workloadAssumption` are TWO SEPARATE
   * declared terms per both decision tickets — never collapse them into one
   * blended note. Neither is a tier input (same audit-only status as
   * `dutyCycle`/`hardwareIdentityDefaults` above); they exist so an auditor
   * can see, and revise, each fact independently.
   */
  gpu: z
    .object({
      name: z.string(),
      units: z.number().int().positive(),
      unitsSource: z.enum(ACCELERATOR_UNITS_SOURCES),
      /** SIL-388 — capacity is an upper bound, never a populated count. */
      populationAssumption: z.string(),
      tdpWatts: z.number().positive(),
      workloadDerateFactor: z.number().positive(),
      /** `tdpWatts * workloadDerateFactor`, PER accelerator. */
      avgPowerPerUnitWatts: z.number().positive(),
      /** SIL-389 — the 50% derate, cited to BoaviztAPI's own archetype
       * workload assumption, not our own telemetry (rejected as synthetic). */
      workloadAssumption: z.string(),
    })
    .nullable()
    .default(null),
  /** Decision 12 / Accepted risks: synthetic telemetry can earn `measured`;
   * this flag makes such rows identifiable without gating anything. */
  orgIsTest: z.boolean(),
});
export type UsePhaseInputProvenance = z.infer<typeof usePhaseInputProvenanceSchema>;

// ---------------------------------------------------------------------------
// Measured-power qualification gate
// ---------------------------------------------------------------------------

/**
 * Minimum calendar days an observation window must span before power can
 * qualify as "measured" for tier purposes — "≥1 full calendar month" from
 * the plan. Reasoned, not calibrated: there is no real power telemetry to
 * calibrate against yet, so this constant is expected to move. Named and
 * exported on its own (not inlined) for exactly that reason.
 */
export const MIN_MEASURED_POWER_WINDOW_DAYS = 30;

/**
 * Minimum fraction of calendar days in the window that must actually carry
 * telemetry — "≥80% daily coverage" from the plan. Same caveat as
 * `MIN_MEASURED_POWER_WINDOW_DAYS`: reasoned, not calibrated, will move.
 *
 * This is a DIFFERENT, STRICTER gate than
 * `MIN_OBSERVED_SAMPLES_FOR_BOUNDS` (`use-phase-math.ts`, = 5 samples). That
 * one decides whether a p10/p90 band is statistically defensible once power
 * is already measured; this one decides whether power counts as "measured"
 * at all for the accuracy tier. A window can clear this coverage gate with
 * only 5 non-null days out of a required-minimum 30-day window (16.7%,
 * failing THIS gate) — the two are independent and must not be conflated.
 */
export const MIN_MEASURED_POWER_COVERAGE_RATIO = 0.8;

/**
 * Whether the power term qualifies as "measured" for accuracy-tier purposes.
 * Requires all three: `power_basis = 'telemetry_measured'`, a window of at
 * least `MIN_MEASURED_POWER_WINDOW_DAYS` calendar days, and coverage
 * (`daysWithData / calendarDaysInWindow`) of at least
 * `MIN_MEASURED_POWER_COVERAGE_RATIO`. Both boundary values are inclusive
 * (`>=`) — exactly 30 days and exactly 80% coverage both qualify.
 *
 * @pure
 */
export function isMeasuredPowerQualified(
  power: UsePhaseInputProvenance['power'],
): boolean {
  if (power.basis !== 'telemetry_measured') return false;
  if (power.calendarDaysInWindow < MIN_MEASURED_POWER_WINDOW_DAYS) return false;
  if (power.calendarDaysInWindow === 0) return false; // guard div-by-zero
  const coverage = power.daysWithData / power.calendarDaysInWindow;
  return coverage >= MIN_MEASURED_POWER_COVERAGE_RATIO;
}

/**
 * Location is "real" for the `region_country_code`, `data_center_address_country`
 * and `seller_org_address_country` rungs — the org rung is one join further
 * out than the data-center rungs but is still a real declared location, per
 * the ticket. Only the `default_wor` floor counts as defaulted.
 *
 * @pure
 */
export function isLocationReal(rung: UsageLocationRung): boolean {
  return rung !== 'default_wor';
}

/**
 * PUE counts as "supplied" (a real tier input, not defaulted) only for the
 * two customer/org-declared rungs of the PUE ladder
 * (`customer_dc` -> `org` -> `industry_default`). A published industry
 * default is exactly as material a defaulted term as a defaulted grid
 * factor, per the plan.
 *
 * @pure
 */
export function isPueSupplied(source: UsePhasePueSource): boolean {
  return source === 'customer_dc' || source === 'org';
}

// ---------------------------------------------------------------------------
// Accuracy tier — weakest of three (power, location, PUE)
// ---------------------------------------------------------------------------

export const USE_PHASE_ACCURACY_TIER_VALUES = [
  'measured',
  'partial',
  'modeled',
  'indicative',
] as const;
export type UsePhaseAccuracyTier = (typeof USE_PHASE_ACCURACY_TIER_VALUES)[number];

/**
 * Weakest-of-three tier derivation over `input_provenance`. Pure, run at
 * read time on every request — see module header for why nothing is stored.
 *
 * | Tier | Power | Location | PUE |
 * |---|---|---|---|
 * | `measured` | measured | real | supplied |
 * | `partial` | measured | one or both of location/PUE defaulted | |
 * | `modeled` | archetype | real | any |
 * | `indicative` | archetype | defaulted (`WOR` floor) | any |
 *
 * **Underspecified case, resolved here:** the ticket's table only names
 * `partial` for "exactly one of location/PUE defaulted." The full 2x2x2
 * matrix also contains (measured power, location defaulted, PUE defaulted)
 * — both weak — which the table does not assign. Because `modeled` and
 * `indicative` are defined in terms of ARCHETYPE power specifically, and
 * this cell still has measured power, it cannot be either of those; it is
 * classified `partial` here (the weakest tier reachable while power stays
 * measured). Flagged for product sign-off rather than silently assumed.
 *
 * @pure
 */
export function deriveUsePhaseAccuracyTier(
  provenance: UsePhaseInputProvenance,
): UsePhaseAccuracyTier {
  const powerMeasured = isMeasuredPowerQualified(provenance.power);
  const locationReal = isLocationReal(provenance.location.rung);
  const pueSupplied = isPueSupplied(provenance.pue.source);

  if (!powerMeasured) {
    return locationReal ? 'modeled' : 'indicative';
  }
  return locationReal && pueSupplied ? 'measured' : 'partial';
}

// ---------------------------------------------------------------------------
// Reporting eligibility — stable, additive-only, published contract
// ---------------------------------------------------------------------------

/**
 * Additive-only published contract, like `CARBON_EXPORT_COLUMNS` — treat
 * these string values as an external API. Never remove or repurpose a
 * value; only append.
 *
 * `not_computed_pending_first_computation`, `not_computed_unsupported_
 * asset_class`, and `not_computed_no_archetype_without_telemetry` (SIL-368)
 * are NOT reachable from `deriveReportingEligibility` — there is no
 * `input_provenance` to parse in any of the three cases (no snapshot yet /
 * asset class never gets one / asset class needs telemetry it doesn't have
 * yet). Callers with no snapshot row choose directly among these three
 * constants via `resolveUsePhaseNotComputedReason` (`dto/use-phase.ts`);
 * they exist here so every eligibility value has exactly one home and
 * export code never invents an ad hoc string.
 */
export const USE_PHASE_REPORTING_ELIGIBILITY_VALUES = [
  'reportable',
  'screening_estimate_default_location',
  'screening_estimate_default_pue',
  'screening_estimate_default_power',
  'screening_estimate_default_power_and_location',
  'not_computed_pending_first_computation',
  'not_computed_unsupported_asset_class',
  'not_computed_no_archetype_without_telemetry',
] as const;
export type UsePhaseReportingEligibility =
  (typeof USE_PHASE_REPORTING_ELIGIBILITY_VALUES)[number];

/** The subset of the enum reachable from a parsed `input_provenance`. */
type ComputedReportingEligibility = Exclude<
  UsePhaseReportingEligibility,
  | 'not_computed_pending_first_computation'
  | 'not_computed_unsupported_asset_class'
  | 'not_computed_no_archetype_without_telemetry'
>;

/**
 * Maps a derived accuracy tier (+ which term was defaulted, for `partial`)
 * to the stable reporting-eligibility enum. Every tier maps to exactly one
 * eligibility value — see module docstring for why the two `not_computed_*`
 * values are out of scope for this function.
 *
 * For the underspecified `partial` cell where BOTH location and PUE are
 * defaulted (see `deriveUsePhaseAccuracyTier`), this resolves to
 * `screening_estimate_default_location`: a defaulted grid factor changes the
 * reported figure by a country-to-country factor that can span an order of
 * magnitude, versus a defaulted PUE's bounded `[1.0, 3.0]` multiplier — the
 * location default is the more material caveat to surface. Flagged
 * alongside the tier-derivation underspecification for product sign-off.
 *
 * @pure
 */
export function deriveReportingEligibility(
  provenance: UsePhaseInputProvenance,
): ComputedReportingEligibility {
  const tier = deriveUsePhaseAccuracyTier(provenance);

  switch (tier) {
    case 'measured':
      return 'reportable';
    case 'modeled':
      return 'screening_estimate_default_power';
    case 'indicative':
      return 'screening_estimate_default_power_and_location';
    case 'partial': {
      const locationReal = isLocationReal(provenance.location.rung);
      const pueSupplied = isPueSupplied(provenance.pue.source);
      if (!locationReal) return 'screening_estimate_default_location';
      if (!pueSupplied) return 'screening_estimate_default_pue';
      // Unreachable given deriveUsePhaseAccuracyTier's contract (partial
      // implies at least one of location/pue defaulted when power is
      // measured), kept as a fail-closed default rather than a thrown error
      // so a future refactor of the tier function can't silently produce an
      // un-mapped eligibility value.
      return 'screening_estimate_default_location';
    }
  }
}
